import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { BusStop } from '../models/bus-stop';
import { Kid } from '../models/kid';
import { KidSport } from '../models/kid_sport';
import { Level } from '../models/level';
import { Parent } from '../models/parent';
import { Sport } from '../models/sport';
import { dateToDB, dateToView } from '../shared/utils';

@Injectable({
  providedIn: 'root'
})
export class KidService {
  private readonly _baseUrl = `${environment.api}/kids`;

  constructor(private http: HttpClient) { }

  public all = async (): Promise<KidData> => {
    const data = await firstValueFrom(this.http.get<KidData>(`${this._baseUrl}/all`));
    data.kids.forEach(k => k.birthDate = dateToView(k.birthDate));
    return data;
  }
  public allKids = async (): Promise<Kid[]> => {
    const kids = await firstValueFrom(this.http.get<Kid[]>(`${this._baseUrl}/all-kids`));
    kids.forEach(k => k.birthDate = dateToView(k.birthDate));
    return kids;
  }
  public add = async (data: any): Promise<Kid> => {
    data.birthDate = dateToDB(data.birthDate);
    const kid = await firstValueFrom(this.http.post<Kid>(`${this._baseUrl}/add`, data));
    kid.birthDate = dateToView(kid.birthDate);
    return kid;
  }
  public update = async (data: any): Promise<Kid> => {
    data.birthDate = dateToDB(data.birthDate);
    const kid = await firstValueFrom(this.http.put<Kid>(`${this._baseUrl}/update`, data));
    kid.birthDate = dateToView(kid.birthDate);
    return kid;
  }
  public kidInfo = async (kidId: string): Promise<Kid> => {
    const kid = await firstValueFrom(this.http.get<Kid>(`${this._baseUrl}/kid-info/${kidId}`));
    kid.birthDate = dateToView(kid.birthDate);
    for (const p of kid.packages) {
      p.depositDueDate = dateToView(p.depositDueDate)!;
      for (const participation of p.participations) {
        participation.date = dateToView(participation.date)!;
      }
      for (const payment of p.payments) {
        payment.date = dateToView(payment.date)!;
      }
    }
    return kid;
  }
  public isOldCustomer = async (kidId: string | undefined): Promise<boolean> => {
    const result = await firstValueFrom(this.http.get<boolean>(`${this._baseUrl}/kid-is-old-customer/${kidId}`));
    return result.toString() === "true";
  }

  public addSport = async (data: any): Promise<KidSport> => {
    return await firstValueFrom(this.http.post<KidSport>(`${this._baseUrl}/add-kid-sport`, data));
  }
  public updateSport = async (data: any): Promise<KidSport> => {
    return await firstValueFrom(this.http.put<KidSport>(`${this._baseUrl}/update-kid-sport`, data));
  }
  public deleteSport = async (data: any): Promise<boolean> => {
    return await firstValueFrom(this.http.delete<boolean>(`${this._baseUrl}/delete-kid-sport`, { body: { data: { id: data } } }));
  }
}

export interface KidData {
  kids: Kid[];
  parents: Parent[];
  busStops: BusStop[];
  kidSports: KidSport[];
  sports: Sport[];
  levels: Level[];
}